/**
 * SKINOLOGY — Custom Theme JavaScript
 * Minimal, non-blocking, modern ES6
 * Handles: scroll reveal, routine ATC, advisory handler, comparison slider, header behavior
 */

'use strict';

/* ── SCROLL REVEAL ─────────────────────────────────────── */
function initScrollReveal() {
  const elements = document.querySelectorAll('.sk-reveal');
  if (!elements.length) return;

  if (!('IntersectionObserver' in window)) {
    elements.forEach((el) => el.classList.add('is-visible'));
    return;
  }

  const isSnapActive = document.body.classList.contains('sk-snap-enabled');
  const scrollContainer = isSnapActive && window.innerWidth >= 990 ? document.querySelector('.page-wrapper') : null;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    },
    { root: scrollContainer, threshold: 0.08, rootMargin: '0px 0px -20px 0px' }
  );

  elements.forEach((el) => observer.observe(el));
}

/* ── ROUTINE ADD ALL TO CART ───────────────────────────── */
function initRoutineAddAll() {
  const btn = document.querySelector('[data-routine-add-all]');
  if (!btn) return;

  btn.addEventListener('click', async () => {
    const productForms = document.querySelectorAll('[data-routine-variant-id]');
    const items = [];

    productForms.forEach((form) => {
      const variantId = form.dataset.routineVariantId;
      if (variantId && variantId !== 'null' && variantId !== '') {
        const idNum = parseInt(variantId, 10);
        if (!isNaN(idNum) && idNum > 0) {
          items.push({ id: idNum, quantity: 1 });
        }
      }
    });

    if (!items.length) return;

    btn.disabled = true;
    const defaultText = btn.dataset.defaultText || btn.textContent || 'Agregar toda la rutina';
    btn.textContent = btn.dataset.loadingText || 'Agregando...';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) throw new Error('Cart error');

      // Trigger Shopify cart drawer refresh event
      document.dispatchEvent(new CustomEvent('cart:refresh'));

      // Open cart drawer if present
      const cartDrawer = document.querySelector('theme-drawer#cart-drawer') || document.querySelector('[data-cart-drawer]');
      if (cartDrawer && typeof cartDrawer.show === 'function') {
        cartDrawer.show();
      }

      btn.textContent = btn.dataset.successText || '¡Agregados al carrito!';
      setTimeout(() => {
        btn.disabled = false;
        btn.textContent = defaultText;
      }, 3000);
    } catch (err) {
      console.error('[SKINOLOGY] Routine ATC error:', err);
      btn.disabled = false;
      btn.textContent = defaultText;
    }
  });
}

/* ── COMPARISON SLIDER ─────────────────────────────────── */
function initComparisonSliders() {
  document.querySelectorAll('.sk-comparison-slider').forEach((slider) => {
    const afterEl = slider.querySelector('.sk-comparison-slider__after');
    const handle = slider.querySelector('.sk-comparison-slider__handle');
    if (!afterEl || !handle) return;

    let isDragging = false;

    function setPosition(clientX) {
      const rect = slider.getBoundingClientRect();
      if (rect.width === 0) return;
      const pos = Math.min(Math.max(((clientX - rect.left) / rect.width) * 100, 0), 100);
      afterEl.style.clipPath = 'inset(0 ' + (100 - pos).toFixed(2) + '% 0 0)';
      handle.style.left = pos.toFixed(2) + '%';
    }

    slider.addEventListener('mousedown', (e) => {
      isDragging = true;
      setPosition(e.clientX);
    });

    slider.addEventListener('touchstart', (e) => {
      isDragging = true;
      if (e.touches && e.touches[0]) {
        setPosition(e.touches[0].clientX);
      }
    }, { passive: true });

    document.addEventListener('mousemove', (e) => {
      if (isDragging) setPosition(e.clientX);
    });

    document.addEventListener('touchmove', (e) => {
      if (isDragging && e.touches && e.touches[0]) {
        setPosition(e.touches[0].clientX);
      }
    }, { passive: true });

    document.addEventListener('mouseup', () => { isDragging = false; });
    document.addEventListener('touchend', () => { isDragging = false; });
  });
}

/* ── ADVISORY CTA HANDLER ──────────────────────────────── */
function initAdvisoryCTA() {
  document.querySelectorAll('[data-advisory-type="whatsapp"]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      const phone = (btn.dataset.advisoryPhone || '').replace(/[^0-9]/g, '');
      const defaultMsg = 'Hola, me gustaría recibir asesoría personalizada para el cuidado de mi piel.';
      const message = encodeURIComponent(btn.dataset.advisoryMessage || defaultMsg);
      if (phone) {
        e.preventDefault();
        const url = 'https://wa.me/' + phone + '?text=' + message;
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    });
  });
}

/* ── HEADER SCROLL BEHAVIOR ────────────────────────────── */
function initHeaderBehavior() {
  const header = document.querySelector('#header-component') || document.querySelector('.header-section');
  if (!header) return;

  function onScroll() {
    const currentScroll = window.scrollY || document.documentElement.scrollTop || 0;
    if (currentScroll > 60) {
      header.setAttribute('scrolled', '');
    } else {
      header.removeAttribute('scrolled');
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true });
}

/* ── SNAP HEIGHT SYNC ──────────────────────────────────── */
function initSnapHeightSync() {
  if (!document.body.classList.contains('sk-snap-enabled')) return;

  function sync() {
    window.requestAnimationFrame(() => {
      const header = document.querySelector('#header-component') || document.querySelector('header') || document.querySelector('.header-section');
      const headerHeight = header ? header.offsetHeight : 66;
      if (headerHeight > 0) {
        document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
        document.body.style.setProperty('--header-height', `${headerHeight}px`);
        const snapHeight = window.innerHeight - headerHeight;
        document.documentElement.style.setProperty('--sk-snap-height', `${snapHeight}px`);
        document.body.style.setProperty('--sk-snap-height', `${snapHeight}px`);
      }
    });
  }

  sync();
  window.addEventListener('resize', sync, { passive: true });
}

/* ── WHATSAPP BOOKING BUTTONS HANDLER ───────────────────── */
function initBookingButtons() {
  const WA_NUMBER = '13465808436';

  function buildWhatsAppUrl(serviceName) {
    const text = serviceName
      ? `Hola Skinology, deseo reservar una cita para el servicio de: ${serviceName}.`
      : 'Hola Skinology, deseo agendar una cita de valoración / reservar consulta.';
    return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(text)}`;
  }

  // 1. Asegurar enlaces en tarjetas de servicios (solo elementos específicos para evitar reflow masivo)
  function enhanceBookingElements() {
    document.querySelectorAll('.sk-service-card').forEach((card) => {
      const titleEl = card.querySelector('.sk-service-card__title');
      const serviceName = titleEl ? titleEl.textContent.trim() : '';
      const bookBtn = card.querySelector('.sk-service-book-btn');
      if (bookBtn && serviceName) {
        bookBtn.setAttribute('target', '_blank');
        bookBtn.setAttribute('rel', 'noopener noreferrer');
        if (!bookBtn.href || bookBtn.href.includes('/pages/contacto') || !bookBtn.href.includes('wa.me')) {
          bookBtn.href = buildWhatsAppUrl(serviceName);
        }
      }
    });
  }

  enhanceBookingElements();

  // 2. Delegación de eventos global pasiva para asegurar reservas sin recorrer todo el DOM al cargar
  document.addEventListener('click', (e) => {
    const bookBtn = e.target.closest('.sk-service-book-btn, .sk-modal-book-btn');
    if (bookBtn) {
      const card = bookBtn.closest('.sk-service-card') || bookBtn.closest('.sk-modal-info-col') || document.querySelector('.sk-modal-info-col');
      let serviceTitle = '';
      if (card) {
        const titleEl = card.querySelector('.sk-service-card__title, .sk-modal-title');
        if (titleEl) serviceTitle = titleEl.textContent.trim();
      }
      if (serviceTitle && (!bookBtn.href || !bookBtn.href.includes('wa.me') || bookBtn.href.includes('/pages/contacto'))) {
        e.preventDefault();
        window.open(buildWhatsAppUrl(serviceTitle), '_blank', 'noopener,noreferrer');
      }
    }
  });
}

/* ── INITIALIZATION ────────────────────────────────────── */
function init() {
  initScrollReveal();
  initHeaderBehavior();
  initSnapHeightSync();
  initBookingButtons();

  // Módulos no críticos diferidos en tiempo libre del hilo principal
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(() => {
      initRoutineAddAll();
      initComparisonSliders();
      initAdvisoryCTA();
    });
  } else {
    setTimeout(() => {
      initRoutineAddAll();
      initComparisonSliders();
      initAdvisoryCTA();
    }, 40);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

